import { ELanguageType, ETextAnchor } from '@hts/shared/enums';
import { useDynamicFont } from '@hts/shared/libs';
import { IDefaultFontInput } from '@hts/shared/types';
import { getTextAlignFromAnchor } from '@hts/shared/utils';
import React, { useCallback } from 'react';
import { Group, Text } from 'react-konva';
import { useSetTextCoordinatesFromSVG } from '../../libs';
import {
  INewFieldItem,
  IOnChangeContainerSizesData,
  IOnRelalculatedByTextAnchorData,
} from '../../types';
import { AutoSizeRect } from './AutoSizeRect';

interface ICardTextFieldProps {
  isActive: boolean;
  item: INewFieldItem;
  index: number;
  defaultHebrewFont?: IDefaultFontInput;
  defaultEnglishFont?: IDefaultFontInput;
  isForExport?: boolean;
  onRelalculatedByTextAnchor: (data: IOnRelalculatedByTextAnchorData) => void;
  onChangeContainerSizes: (data: IOnChangeContainerSizesData) => void;
}

export const CardTextField: React.FC<ICardTextFieldProps> = ({
  isActive,
  item,
  defaultHebrewFont,
  defaultEnglishFont,
  isForExport,
  onRelalculatedByTextAnchor,
  onChangeContainerSizes,
}) => {
  const {
    id,
    text,
    text_position_x,
    text_position_y,
    text_rotation,
    font_color,
    blockHeight,
    blockWidth,
    textAlignment,
    text_alignment,
    recalculatedByTextAnchor,
    font_size,
    changedFontSize,
    defaultText,
    text_lang,
    initial_text_lang,
    skew,
    letter_spacing,
    font,
    group,
  } = item;

  const direction = text_lang === ELanguageType.HEBREW ? 'rtl' : 'ltr';
  const defaultFont = text_lang === ELanguageType.HEBREW ? defaultHebrewFont : defaultEnglishFont;
  const fontFamily = initial_text_lang === text_lang ? font : defaultFont;
  const fontSize = Number(font_size.replace('px', ''));
  const align = textAlignment || getTextAlignFromAnchor(text_alignment || ETextAnchor.MIDDLE);
  const rotation = item.group?.textData?.rotation || text_rotation;

  const dynamicFontSize = changedFontSize || fontSize;

  const loadedFontName = useDynamicFont({
    fontUrl: fontFamily ? fontFamily.fileUrl || '' : undefined,
    fontFamily: fontFamily?.name,
  });

  const x = group?.textData?.x || text_position_x;
  const y = group?.textData?.y || text_position_y;
  const width = blockWidth || 0;
  const height = blockHeight || 0;

  const changeContainerSizesStates = useCallback(
    (width: number, height: number) => {
      onChangeContainerSizes({ id, width, height });
    },
    [id, onChangeContainerSizes]
  );

  const changeTextCoordinates = useCallback(
    (x: number, y: number) => {
      onRelalculatedByTextAnchor({ id, x, y });
    },
    [id, onRelalculatedByTextAnchor]
  );

  useSetTextCoordinatesFromSVG({
    containerWidth: width,
    containerHeight: height,
    containerX: text_position_x,
    containerY: text_position_y,
    textAnchor: text_alignment || undefined,
    isRecalculatedByTextAnchor: recalculatedByTextAnchor,
    changeCoordinates: changeTextCoordinates,
  });

  return (
    <Group id={id} x={x} y={y} listening={false}>
      <AutoSizeRect
        isActive={isActive && !isForExport}
        existedSizes={Boolean(width || height)}
        text={defaultText}
        containerWidth={width}
        containerHeight={height}
        fontFamily={loadedFontName || undefined}
        fontSize={fontSize}
        rotation={rotation}
        setContainerSizes={changeContainerSizesStates}
      />

      <Text
        text={text}
        fill={font_color}
        wrap="none"
        fontSize={dynamicFontSize}
        rotation={rotation}
        fontFamily={loadedFontName || undefined}
        width={width}
        height={height}
        verticalAlign="middle"
        skewX={skew}
        letterSpacing={letter_spacing}
        align={align}
        direction={direction}
      />
    </Group>
  );
};
